import React, { useMemo, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Flex,
  IconButton,
  Select,
} from "@chakra-ui/react";
import { useTable, useFilters, useSortBy } from "react-table";
import { ListFilter } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import NoDataFound from "components/NoDataGraphics/NoDataFound";
import LoadingSpinner from "components/Loading/LoadingSpinner";

const DropdownFilter = ({
  column: { filterValue, setFilter, id },
  onTypeFilterChange,
}) => {
  const options =
    id === "user_type"
      ? ["All", "Admin", "Business", "Driver", "Individual", "Agent"]
      : ["All", "Active", "Inactive", "Suspended"];

  const handleChange = (e) => {
    const value = e.target.value;

    setFilter(value === "All" ? undefined : value);

    if (id === "user_type" && onTypeFilterChange) {
      onTypeFilterChange(value);
    }
  };


  return (
    <Select
      value={filterValue || "All"}
      onChange={handleChange}
      placeholder="Select an option"
      size="sm"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </Select>
  );
};

const ColumnFilter = ({ column, onSearch }) => {
  const [value, setValue] = useState("");

  return (
    <Input
      value={value}

      onChange={(e) => {
        const v = e.target.value;
        setValue(v);

        onSearch(column.id, v);
      }}
      placeholder="Search..."
      size="sm"
      variant="flushed"
    />
  );
};

const ReusableTable = ({ columnsData, tableData, isLoading, error, onTypeFilterChange, onSearch, emptyMessage = "No data found." }) => {
  const [filterVisibility, setFilterVisibility] = useState({});

  const defaultColumn = useMemo(() => ({ Filter: ColumnFilter }), []);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns: columnsData,
        data: tableData,
        defaultColumn,
      },
      useFilters,
      useSortBy
    );

  const toggleFilterVisibility = (columnId) => {
    setFilterVisibility((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  if (error) {
    return <NoDataFound message="Error loading data, please try again later." />;
  }


  return (
    <Box>
      <TableContainer>
        <Table {...getTableProps()} variant="simple" size="md">
          <Thead>
            {isLoading && tableData.length === 0 ? (
              <Tr>
                {columnsData.map((_, index) => (
                  <Th key={index}>
                    <Skeleton height={6} width="100%" />
                  </Th>
                ))}
              </Tr>
            ) : (
              headerGroups.map((headerGroup) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                  {headerGroup.headers.map((column) => (
                    <Th {...column.getHeaderProps()} key={column.id}>
                      <Flex align="center">
                        <Box>{column.render("Header")}</Box>
                        {column.canFilter &&
                          column.render("Header") !== "Actions" && (
                            <IconButton
                              aria-label="Filter"
                              icon={<ListFilter size={16} />}
                              size="xs"
                              variant="ghost"
                              color="gray.400"
                              onClick={() => toggleFilterVisibility(column.id)}
                              ml={2}
                            />
                          )}
                      </Flex>
                      {filterVisibility[column.id] &&
                        (column.id === "user_type" || column.id === "status" ? (
                          <DropdownFilter column={column} onTypeFilterChange={onTypeFilterChange} />
                        ) : (
                          <ColumnFilter
                            column={column}
                            onSearch={onSearch}
                          />
                        ))}
                    </Th>
                  ))}
                </Tr>
              ))
            )}
          </Thead>

          <Tbody {...getTableBodyProps()}>
            {!isLoading && rows.length === 0 ? (
              <Tr>
                <Td colSpan={columnsData.length}>
                  <NoDataFound message={emptyMessage} />
                </Td>
              </Tr>
            ) : (
              rows.map((row, index) => {
                prepareRow(row);

                return (
                  <Tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell) => (
                      <Td
                        {...cell.getCellProps()}
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="400"
                      >
                        {cell.render("Cell")}
                      </Td>
                    ))}
                  </Tr>
                );
              })
            )}
            {isLoading && tableData.length > 0 && (
              <Tr>
                <Td colSpan={columnsData.length}>
                  <LoadingSpinner />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReusableTable;
